import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import moment from 'moment';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import FilterDatePicker from '../Filter/components/FilterDateInput';
import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';

export interface PresetPeriodRange {
    beginIn: number;
    endIn: number;
}

type Preset = {
    key: string;
    getRange: () => PresetPeriodRange;
};

const column1: Preset[] = [
    { key: 'last5min', getRange: () => ({ beginIn: moment().subtract(5, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last15min', getRange: () => ({ beginIn: moment().subtract(15, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last30min', getRange: () => ({ beginIn: moment().subtract(30, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last1h', getRange: () => ({ beginIn: moment().subtract(1, 'hour').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last6h', getRange: () => ({ beginIn: moment().subtract(6, 'hours').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last12h', getRange: () => ({ beginIn: moment().subtract(12, 'hours').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last24h', getRange: () => ({ beginIn: moment().subtract(24, 'hours').valueOf(), endIn: moment().valueOf() }) },
];

const column2: Preset[] = [
    { key: 'yesterday', getRange: () => ({ beginIn: moment().subtract(1, 'day').startOf('day').valueOf(), endIn: moment().subtract(1, 'day').endOf('day').valueOf() }) },
    { key: 'today', getRange: () => ({ beginIn: moment().startOf('day').valueOf(), endIn: moment().endOf('day').valueOf() }) },
    { key: 'thisWeek', getRange: () => ({ beginIn: moment().startOf('week').valueOf(), endIn: moment().endOf('week').valueOf() }) },
    { key: 'thisMonth', getRange: () => ({ beginIn: moment().startOf('month').valueOf(), endIn: moment().endOf('month').valueOf() }) },
    { key: 'thisYear', getRange: () => ({ beginIn: moment().startOf('year').valueOf(), endIn: moment().endOf('year').valueOf() }) },
];

const column3: Preset[] = [
    { key: 'lastWeek', getRange: () => ({ beginIn: moment().subtract(1, 'week').startOf('week').valueOf(), endIn: moment().subtract(1, 'week').endOf('week').valueOf() }) },
    { key: 'lastMonth', getRange: () => ({ beginIn: moment().subtract(1, 'month').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastBimester', getRange: () => ({ beginIn: moment().subtract(2, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastQuarter', getRange: () => ({ beginIn: moment().subtract(3, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastSemester', getRange: () => ({ beginIn: moment().subtract(6, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastYearFIlter', getRange: () => ({ beginIn: moment().subtract(1, 'year').startOf('year').valueOf(), endIn: moment().subtract(1, 'year').endOf('year').valueOf() }) },
];

function PeriodSelector<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
    T extends { from: Date | null; to: Date | null; },
>({ name }: { name: TName; }) {
    const form = useFormContext<TFieldValues>();

    const { field } = useController({
        control: form.control,
        name,
    });

    const value = field.value as T;

    const [preset, setPreset] = useState('');
    const [beginDate, setBeginDate] = useState<Date | null>(value.from ? new Date(value.from) : null);
    const [beginTime, setBeginTime] = useState(value.from ? moment(value.from).format('HH:mm') : '');
    const [endDate, setEndDate] = useState<Date | null>(value.to ? new Date(value.to) : null);
    const [endTime, setEndTime] = useState(value.to ? moment(value.to).format('HH:mm') : '');

    const { styles } = useStyles(styleSheet);
    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const isEmpty = preset === '' && !beginDate && !endDate;

    const onClear = () => {
        setPreset('');
        setBeginDate(null);
        setBeginTime('');
        setEndDate(null);
        setEndTime('');
    };

    const formatDateTime = (date: Date | null, time: string) =>
        date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;


    useEffect(() => {
        const from = formatDateTime(beginDate, beginTime ? `${beginTime}:00` : '00:00:00');
        const to = formatDateTime(endDate, endTime ? `${endTime}:59` : '23:59:59');

        field.onChange({
            from: from ? new Date(from) : null,
            to: to ? new Date(to) : null,
        });

    }, [beginDate, beginTime, endDate, endTime]);

    const handleSelectPreset = (item: Preset) => {
        if (preset === item.key) {
            setPreset('');
        } else {
            setPreset(item.key);
        }

        const range = item.getRange();

        if (range) {
            setBeginDate(new Date(range.beginIn));
            setBeginTime(moment(range.beginIn).format('HH:mm'));
            setEndDate(new Date(range.endIn));
            setEndTime(moment(range.endIn).format('HH:mm'));
        } else if (preset === '') {
            onClear();
        }
    };

    const renderItem = (item: Preset) => {
        const isSelected = preset === item.key;
        return (
            <TouchableOpacity
                key={item.key}
                style={styles.item}
                onPress={() => handleSelectPreset(item)}
            >
                <MyAppText style={[styles.itemText, isSelected && styles.itemTextSelected]}>
                    {translate(item.key)}
                </MyAppText>
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <View style={styles.clearRow}>
                <TouchableOpacity onPress={onClear} disabled={isEmpty}>
                    <MyAppText style={[styles.clearSelection, isEmpty && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            <View style={styles.columns}>
                <View style={styles.column}>{column1.map(renderItem)}</View>
                <View style={styles.column}>{column2.map(renderItem)}</View>
                <View style={styles.column}>{column3.map(renderItem)}</View>
            </View>

            <View style={styles.customContainer}>
                <View style={{ zIndex: estimatedStartZIndex }}>
                    <FilterDatePicker
                        date={beginDate}
                        onChangeDate={(value) => {
                            setPreset('');
                            setBeginDate(value);
                        }}
                        time={beginTime}
                        onChangeTime={(value) => {
                            setPreset('');
                            setBeginTime(value);
                        }}
                        label={translate('createdAt')}
                        onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                        onCalendarClose={() => setEstimatedStartZIndex(2)}
                    />
                </View>
                <View style={{ zIndex: estimatedFinishZIndex }}>
                    <FilterDatePicker
                        date={endDate}
                        onChangeDate={(value) => {
                            setPreset('');
                            setEndDate(value);
                        }}
                        time={endTime}
                        onChangeTime={(value) => {
                            setPreset('');
                            setEndTime(value);
                        }}
                        label={translate('to')}
                        onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                        onCalendarClose={() => setEstimatedFinishZIndex(1)}
                    />
                </View>
            </View>
        </View>
    );
}

PeriodSelector.getCount = (value: { from: number | string | Date | null; to: number | string | Date | null; }) => {
    return value.from !== null || value.to !== null ? 1 : 0;
};

export default PeriodSelector;

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 16,
    },
    clearRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    columns: {
        flexDirection: 'row',
    },
    column: {
        flex: 1,
        gap: 8,
    },
    item: {},
    itemText: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    itemTextSelected: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        textDecorationLine: 'underline',
    },
    customContainer: {
        gap: 12,
    },
}));
